/**
 * ClientCard - Component for displaying individual client portfolio summary
 * Used in client lists and portfolio overviews
 */

import React from 'react';
import { User, AlertCircle, ChevronRight } from 'lucide-react';
import HealthScoreGauge from '../HealthScoreGauge';
import TrendIndicator from '../atoms/TrendIndicator';

export interface ClientCardProps {
  id: string;
  name: string;
  portfolioValue: number;
  healthScore: number;
  performance?: number;
  performancePeriod?: string;
  lastActivity?: string;
  riskLevel?: 'low' | 'moderate' | 'high';
  needsAttention?: boolean;
  currency?: string;
  className?: string;
  onSelect?: (id: string) => void;
  onAction?: (id: string, action: string) => void;
}

const ClientCard: React.FC<ClientCardProps> = ({
  id,
  name,
  portfolioValue,
  healthScore,
  performance,
  performancePeriod = 'YTD',
  lastActivity,
  riskLevel = 'moderate',
  needsAttention = false,
  currency = 'CHF',
  className = '',
  onSelect,
  onAction
}) => {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('de-CH', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const riskColors = {
    low: 'text-green-600 bg-green-50',
    moderate: 'text-yellow-600 bg-yellow-50',
    high: 'text-red-600 bg-red-50'
  };

  return (
    <div 
      className={`
        bg-white border border-gray-200 rounded-lg p-4
        ${onSelect ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}
        ${needsAttention ? 'border-yellow-400' : ''}
        ${className}
      `}
      onClick={() => onSelect?.(id)}
    >
      {needsAttention && (
        <div className="flex items-center gap-2 mb-3 p-2 bg-yellow-50 rounded-md">
          <AlertCircle className="w-4 h-4 text-yellow-600" />
          <span className="text-sm font-medium text-yellow-800">Needs Attention</span>
        </div>
      )}

      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
            <User className="w-5 h-5 text-gray-600" />
          </div>
          <div>
            <h4 className="font-semibold text-gray-900">{name}</h4>
            {lastActivity && (
              <p className="text-xs text-gray-500">Last activity: {lastActivity}</p>
            )}
          </div>
        </div>
        {onSelect && (
          <ChevronRight className="w-5 h-5 text-gray-400" />
        )}
      </div>

      <div className="grid grid-cols-2 gap-4 mb-3">
        <div>
          <p className="text-xs text-gray-500 mb-1">Portfolio Value</p>
          <p className="text-lg font-bold text-gray-900">
            {formatCurrency(portfolioValue)}
          </p>
          {performance !== undefined && (
            <TrendIndicator
              value={performance}
              showIcon={true}
              showValue={true}
              className="text-sm mt-1"
              suffix={` ${performancePeriod}`}
            />
          )}
        </div>

        <div className="flex justify-end">
          <div className="scale-75 origin-right">
            <HealthScoreGauge score={healthScore} showTooltip={false} />
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">Risk:</span>
          <span className={`text-xs font-medium px-2 py-0.5 rounded ${riskColors[riskLevel]}`}>
            {riskLevel.toUpperCase()}
          </span>
        </div>

        {onAction && (
          <div className="flex gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onAction(id, 'meeting');
              }}
              className="text-xs text-blue-600 hover:text-blue-800 font-medium"
            >
              Prepare Meeting
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onAction(id, 'view');
              }}
              className="text-xs text-gray-600 hover:text-gray-800 font-medium"
            >
              View Details
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientCard;